import React from 'react';
import { Link } from 'react-router-dom';
import { getAuthorProfilePicture } from '../utils/authorProfiles';
import './Resources.css';

const Resources: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="relative z-10 pb-8 bg-white sm:pb-16 md:pb-20 lg:max-w-2xl lg:w-full lg:pb-28 xl:pb-32">
            <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 lg:mt-16 lg:px-8 xl:mt-20">
              <div className="sm:text-center lg:text-left">
                <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
                  <span className="block">Learn Farsi</span>
                  <span className="block text-amber-500">Through Creativity</span>
                </h1>
                <p className="mt-3 text-base text-gray-500 sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-0">
                  Discover Persian language and culture through our unique collection of interactive materials, stories, and multimedia content.
                </p>
                <div className="mt-5 sm:mt-8 sm:flex sm:justify-center lg:justify-start">
                  <div className="rounded-md shadow">
                    <Link 
                      to="/register" 
                      className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-amber-500 hover:bg-amber-600 md:py-4 md:text-lg md:px-10"
                    >
                      Start Learning
                    </Link>
                  </div>
                  <div className="mt-3 sm:mt-0 sm:ml-3">
                    <Link 
                      to="/pathways" 
                      className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-amber-700 bg-amber-100 hover:bg-amber-200 md:py-4 md:text-lg md:px-10"
                    >
                      Explore Materials
                    </Link>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
        <div className="lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
          <img 
            className="h-56 w-full object-cover sm:h-72 md:h-96 lg:w-full lg:h-full" 
            src="https://images.unsplash.com/photo-1547981609-4ab6e592a37d?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80" 
            alt="Persian architecture"
          />
        </div>
      </div>

      {/* Learning Progress */}
      <div className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-amber-500 font-semibold tracking-wide uppercase">Your Progress</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Continue your Farsi journey
            </p>
          </div>
          
          <div className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {/* Progress Card 1 */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-amber-100 rounded-md p-3">
                    <svg className="h-6 w-6 text-amber-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Alphabet Mastery
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        85%
                      </div>
                    </dd>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className="bg-amber-500 h-2.5 rounded-full" style={{width: '85%'}}></div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Progress Card 2 */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-blue-100 rounded-md p-3">
                    <svg className="h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                    </svg>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Vocabulary
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        320 words
                      </div>
                    </dd>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className="bg-blue-500 h-2.5 rounded-full" style={{width: '64%'}}></div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Progress Card 3 */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-purple-100 rounded-md p-3">
                    <svg className="h-6 w-6 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Pronunciation
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        72%
                      </div>
                    </dd>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className="bg-purple-500 h-2.5 rounded-full" style={{width: '72%'}}></div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Progress Card 4 */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-green-100 rounded-md p-3">
                    <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Daily Streak
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        7 days
                      </div>
                    </dd>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className="bg-green-500 h-2.5 rounded-full" style={{width: '100%'}}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Creative Materials Section */}
      <div className="py-12 bg-gray-50 relative">
        <div className="absolute inset-0 opacity-10">
          <div style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fillRule='evenodd'%3E%3Cg fill='%239C92AC' fillOpacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} className="w-full h-full"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="lg:text-center">
            <h2 className="text-base text-amber-500 font-semibold tracking-wide uppercase">Creative Learning</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Explore Our Unique Materials
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Learn Farsi through diverse and engaging content designed to make learning enjoyable and effective.
            </p>
          </div>

          <div className="mt-10">
            <div className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-10">
              {/* Material 1 - Persian Poetry */}
              <div className="relative bg-white p-6 rounded-lg shadow-lg overflow-hidden hover:-translate-y-1 transition-all duration-300">
                <div className="absolute -right-6 -top-6 bg-amber-500 text-white rounded-full h-16 w-16 flex items-center justify-center">
                  <i className="fas fa-feather-alt text-xl"></i>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-amber-100 rounded-md p-3">
                    <i className="fas fa-feather-alt text-amber-600 text-xl"></i>
                  </div>
                  <div className="ml-5">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Persian Poetry</h3>
                    <p className="mt-2 text-base text-gray-500">
                      Explore the beauty of Farsi through classic and modern Persian poetry with interactive translations.
                    </p>
                    <div className="mt-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800">
                        Literary
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 ml-2">
                        Culture
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex justify-between items-center">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <img className="h-10 w-10 rounded-full" src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" alt=""/>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">
                        Sara Johnson
                      </p>
                      <div className="flex space-x-1 text-sm text-gray-500">
                        <span>Poetry Expert</span>
                      </div>
                    </div>
                  </div>
                  <Link 
                    to="/hafez"
                    className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-5 font-medium rounded-md text-amber-700 bg-amber-100 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                  >
                    Explore
                  </Link>
                </div>
              </div>

              {/* Material 2 - Persian Calligraphy */}
              <div className="relative bg-white p-6 rounded-lg shadow-lg overflow-hidden hover:-translate-y-1 transition-all duration-300">
                <div className="absolute -right-6 -top-6 bg-blue-500 text-white rounded-full h-16 w-16 flex items-center justify-center">
                  <i className="fas fa-pen-nib text-xl"></i>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-blue-100 rounded-md p-3">
                    <i className="fas fa-pen-nib text-blue-600 text-xl"></i>
                  </div>
                  <div className="ml-5">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Persian Calligraphy</h3>
                    <p className="mt-2 text-base text-gray-500">
                      Learn Farsi script through the art of calligraphy with interactive stroke guides and practice sheets.
                    </p>
                    <div className="mt-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        Art
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 ml-2">
                        Writing
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex justify-between items-center">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <img className="h-10 w-10 rounded-full" src={getAuthorProfilePicture('Negin Nickparsa')} alt="Negin Nickparsa"/>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">
                        Negin Nickparsa
                      </p>
                      <div className="flex space-x-1 text-sm text-gray-500">
                        <span>Calligraphy Master</span>
                      </div>
                    </div>
                  </div>
                  <Link 
                    to="/alphabet-learning"
                    className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-5 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Explore
                  </Link>
                </div>
              </div>

              {/* Material 3 - Persian Cooking */}
              <div className="relative bg-white p-6 rounded-lg shadow-lg overflow-hidden hover:-translate-y-1 transition-all duration-300">
                <div className="absolute -right-6 -top-6 bg-red-500 text-white rounded-full h-16 w-16 flex items-center justify-center">
                  <i className="fas fa-utensils text-xl"></i>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-red-100 rounded-md p-3">
                    <i className="fas fa-utensils text-red-600 text-xl"></i>
                  </div>
                  <div className="ml-5">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Persian Cooking</h3>
                    <p className="mt-2 text-base text-gray-500">
                      Learn Farsi vocabulary through authentic Persian recipes with step-by-step video instructions.
                    </p>
                    <div className="mt-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                        Food
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 ml-2">
                        Culture
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex justify-between items-center">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <img className="h-10 w-10 rounded-full" src="https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" alt=""/>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">
                        Justin Hanes
                      </p>
                      <div className="flex space-x-1 text-sm text-gray-500">
                        <span>Chef & Instructor</span>
                      </div>
                    </div>
                  </div>
                  <Link 
                    to="/cultural/recipes"
                    className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-5 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Explore
                  </Link>
                </div>
              </div>

              {/* Material 4 - Persian Music */}
              <div className="relative bg-white p-6 rounded-lg shadow-lg overflow-hidden hover:-translate-y-1 transition-all duration-300">
                <div className="absolute -right-6 -top-6 bg-purple-500 text-white rounded-full h-16 w-16 flex items-center justify-center">
                  <i className="fas fa-music text-xl"></i>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-purple-100 rounded-md p-3">
                    <i className="fas fa-music text-purple-600 text-xl"></i>
                  </div>
                  <div className="ml-5">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Persian Music</h3>
                    <p className="mt-2 text-base text-gray-500">
                      Learn Farsi through lyrics of traditional and modern Persian songs with interactive translations.
                    </p>
                    <div className="mt-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                        Music
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-pink-100 text-pink-800 ml-2">
                        Listening
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex justify-between items-center">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <img className="h-10 w-10 rounded-full" src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" alt=""/>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">
                        Farhad Rostami
                      </p>
                      <div className="flex space-x-1 text-sm text-gray-500">
                        <span>Musician</span>
                      </div>
                    </div>
                  </div>
                  <Link 
                    to="/cultural"
                    className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-5 font-medium rounded-md text-purple-700 bg-purple-100 hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  >
                    Explore
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Interactive Learning Section */}
      <div className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-amber-500 font-semibold tracking-wide uppercase">Interactive Learning</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Engage with Farsi Creatively
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Our interactive tools make learning Farsi fun and memorable.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {/* Interactive Card 1 - Flashcards */}
            <div className="group perspective-1000">
              <div className="group-hover:rotate-y-180 preserve-3d transition-transform duration-600 h-full">
                <div className="bg-amber-50 rounded-lg shadow-lg p-6 h-full flex flex-col backface-hidden">
                  <div className="flex-shrink-0 bg-amber-100 rounded-md p-3 inline-flex">
                    <i className="fas fa-layer-group text-amber-600 text-xl"></i>
                  </div>
                  <h3 className="mt-4 text-lg font-medium text-gray-900">Smart Flashcards</h3>
                  <p className="mt-2 text-base text-gray-500">
                    Spaced repetition flashcards with audio pronunciation and example sentences.
                  </p>
                  <div className="mt-auto pt-6">
                    <Link 
                      to="/flashcards"
                      className="text-amber-600 hover:text-amber-500 font-medium"
                    >
                      Try flashcards <span aria-hidden="true">→</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Interactive Card 2 - Dialogue Builder */}
            <div className="group perspective-1000">
              <div className="group-hover:rotate-y-180 preserve-3d transition-transform duration-600 h-full">
                <div className="bg-blue-50 rounded-lg shadow-lg p-6 h-full flex flex-col backface-hidden">
                  <div className="flex-shrink-0 bg-blue-100 rounded-md p-3 inline-flex">
                    <i className="fas fa-comments text-blue-600 text-xl"></i>
                  </div>
                  <h3 className="mt-4 text-lg font-medium text-gray-900">Dialogue Builder</h3>
                  <p className="mt-2 text-base text-gray-500">
                    Build real-life conversations with interactive role-play scenarios.
                  </p>
                  <div className="mt-auto pt-6">
                    <Link 
                      to="/pathways"
                      className="text-blue-600 hover:text-blue-500 font-medium"
                    >
                      Start conversations <span aria-hidden="true">→</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Interactive Card 3 - Writing Practice */}
            <div className="group perspective-1000">
              <div className="group-hover:rotate-y-180 preserve-3d transition-transform duration-600 h-full">
                <div className="bg-purple-50 rounded-lg shadow-lg p-6 h-full flex flex-col backface-hidden">
                  <div className="flex-shrink-0 bg-purple-100 rounded-md p-3 inline-flex">
                    <i className="fas fa-pen-fancy text-purple-600 text-xl"></i>
                  </div>
                  <h3 className="mt-4 text-lg font-medium text-gray-900">Writing Practice</h3>
                  <p className="mt-2 text-base text-gray-500">
                    Interactive writing exercises with stroke-by-stroke guidance.
                  </p>
                  <div className="mt-auto pt-6">
                    <Link 
                      to="/alphabet-grid"
                      className="text-purple-600 hover:text-purple-500 font-medium"
                    >
                      Practice writing <span aria-hidden="true">→</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-amber-500 font-semibold tracking-wide uppercase">Success Stories</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              What Our Learners Say
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {/* Testimonial 1 */}
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <img className="h-12 w-12 rounded-full" src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" alt=""/>
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-gray-900">Emily Johnson</h4>
                  <div className="flex items-center mt-1">
                    <div className="flex items-center">
                      <i className="fas fa-star text-amber-400"></i>
                      <i className="fas fa-star text-amber-400 ml-1"></i>
                      <i className="fas fa-star text-amber-400 ml-1"></i>
                      <i className="fas fa-star text-amber-400 ml-1"></i>
                      <i className="fas fa-star text-amber-400 ml-1"></i>
                    </div>
                    <span className="text-gray-500 text-sm ml-2">3 months learning</span>
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <p className="text-gray-600">
                  "The Persian poetry section completely changed how I approach language learning. Understanding Hafez's poems in their original form is magical!"
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-amber-100 rounded-md p-2">
                    <i className="fas fa-feather-alt text-amber-600"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Favorite Feature</p>
                    <p className="text-sm text-gray-500">Persian Poetry Explorer</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <img className="h-12 w-12 rounded-full" src="https://images.unsplash.com/photo-1519244703995-f4e0f30006d5?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" alt=""/>
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-gray-900">Michael Chen</h4>
                  <div className="flex items-center mt-1">
                    <div className="flex items-center">
                      <i className="fas fa-star text-amber-400"></i>
                      <i className="fas fa-star text-amber-400 ml-1"></i>
                      <i className="fas fa-star text-amber-400 ml-1"></i>
                      <i className="fas fa-star text-amber-400 ml-1"></i>
                      <i className="fas fa-star-half-alt text-amber-400 ml-1"></i>
                    </div>
                    <span className="text-gray-500 text-sm ml-2">5 months learning</span>
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <p className="text-gray-600">
                  "As a visual learner, the calligraphy lessons helped me understand the script much faster than traditional methods. Now I can write my name beautifully in Farsi!"
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-blue-100 rounded-md p-2">
                    <i className="fas fa-pen-nib text-blue-600"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Favorite Feature</p>
                    <p className="text-sm text-gray-500">Calligraphy Practice</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <img className="h-12 w-12 rounded-full" src="https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" alt=""/>
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-gray-900">David Wilson</h4>
                  <div className="flex items-center mt-1">
                    <div className="flex items-center">
                      <i className="fas fa-star text-amber-400"></i>
                      <i className="fas fa-star text-amber-400 ml-1"></i>
                      <i className="fas fa-star text-amber-400 ml-1"></i>
                      <i className="fas fa-star text-amber-400 ml-1"></i>
                      <i className="fas fa-star text-amber-400 ml-1"></i>
                    </div>
                    <span className="text-gray-500 text-sm ml-2">8 months learning</span>
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <p className="text-gray-600">
                  "The cooking videos not only taught me Farsi kitchen vocabulary but also helped me impress my Persian friends with authentic dishes. Best language learning method ever!"
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-red-100 rounded-md p-2">
                    <i className="fas fa-utensils text-red-600"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Favorite Feature</p>
                    <p className="text-sm text-gray-500">Persian Cooking Lessons</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-amber-600">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            <span className="block">Ready to dive into Farsi?</span>
            <span className="block text-amber-200">Start your creative learning journey today.</span>
          </h2>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
            <div className="inline-flex rounded-md shadow">
              <Link 
                to="/register" 
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-amber-600 bg-white hover:bg-amber-50"
              >
                Get Started
              </Link>
            </div>
            <div className="ml-3 inline-flex rounded-md shadow">
              <Link 
                to="/about" 
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-amber-700 bg-opacity-60 hover:bg-opacity-70"
              >
                <i className="fas fa-play-circle mr-2"></i> Watch Demo
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Resources;
